import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { CreditProgressBarWithTooltip } from "@src/appV2/redesign/EmergencyCredits/components/CreditProgressBarWithTooltip";
import { ShieldIcon } from "@src/appV2/redesign/EmergencyCredits/components/ShieldIcon";

import { ScoreDisplay } from "./components/ScoreDisplay";
import { ScoreLineItemsContainer } from "./components/ScoreLineItemsContainer";
import { GRADIENTS, LAYOUT, NEGATIVE_SCORE_COLOR } from "./constants";
import { useClipboardScorePageState } from "./hooks/useClipboardScorePageState";
import { type YourClipboardScorePageProps } from "./types";

export function YourClipboardScorePage(props: YourClipboardScorePageProps) {
  const {
    startScore,
    scoreChanges,
    subtitle,
    gradientVariant,
    showCreditProgressBar,
    creditProgressBarProps,
    creditAmount = 0,
    hasLatenessCredit,
    onContinue,
    isButtonDisabled = false,
  } = props;

  const {
    visibleLineItemCount,
    scoreDisplayProps,
    effectiveCreditProgressBarProps,
    creditLineItem,
    creditState,
    buttonText,
    buttonEnabled,
    handleButtonClick,
  } = useClipboardScorePageState({
    startScore,
    scoreChanges,
    creditAmount,
    hasLatenessCredit,
    creditProgressBarProps,
    isButtonDisabled,
    onContinue,
  });

  useLogEffect(APP_V2_USER_EVENTS.YOUR_CLIPBOARD_SCORE_PAGE_VIEWED, {
    startScore,
    scoreChanges,
    hasLatenessCredit,
  });

  return (
    <PageWrapper
      sx={{
        background: GRADIENTS[gradientVariant],
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack
          spacing={9}
          sx={{
            paddingTop: 10,
            paddingX: 5,
            textAlign: "center",
            alignItems: "center",
          }}
        >
          <Stack spacing={4} alignItems="center">
            <Text
              variant="h3"
              textAlign="center"
              sx={(theme) => ({
                paddingX: 6,
                color:
                  gradientVariant === "negative"
                    ? NEGATIVE_SCORE_COLOR
                    : theme.palette.text.secondary,
              })}
            >
              Your Clipboard Score
            </Text>

            <Text
              semibold
              variant="h6"
              textAlign="center"
              sx={(theme) => ({
                paddingX: 6,
                color: theme.palette.text.secondary,
              })}
            >
              {subtitle}
            </Text>
          </Stack>

          <Stack spacing={7} alignItems="center">
            <ScoreDisplay {...scoreDisplayProps} />

            {showCreditProgressBar && isDefined(effectiveCreditProgressBarProps) && (
              <Stack spacing={1} alignItems="center" sx={{ width: LAYOUT.CONTENT_MAX_WIDTH }}>
                <CreditProgressBarWithTooltip
                  {...effectiveCreditProgressBarProps}
                  showTooltip={false}
                />
                {creditState === "idle" &&
                  effectiveCreditProgressBarProps.currentCount <
                    effectiveCreditProgressBarProps.totalCount && (
                    <Text
                      variant="caption"
                      textAlign="center"
                      sx={(theme) => ({ color: theme.palette.text.tertiary })}
                    >
                      {effectiveCreditProgressBarProps.currentCount}/
                      {effectiveCreditProgressBarProps.totalCount} shifts complete
                    </Text>
                  )}
              </Stack>
            )}
          </Stack>

          <ScoreLineItemsContainer
            startScore={startScore}
            scoreChanges={scoreChanges}
            visibleLineItemCount={visibleLineItemCount}
            creditLineItem={creditLineItem}
          />
        </Stack>
      </Box>

      <DialogFooter orientation="horizontal">
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={!buttonEnabled}
          data-testid="your-clipboard-score-action-button"
          onClick={handleButtonClick}
        >
          {hasLatenessCredit && creditState === "idle" ? (
            <Stack direction="row" alignItems="center" spacing={2}>
              <ShieldIcon
                variant={buttonEnabled ? "filled" : "outlined"}
                sx={{ width: 18, height: 18 }}
              />
              <Box component="span">{buttonText}</Box>
            </Stack>
          ) : (
            buttonText
          )}
        </Button>
      </DialogFooter>
    </PageWrapper>
  );
}
